import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  getCategoryList,
  createCategory,
  updateCategory,
  deleteCategory,
} from "../api/category.api";
import type {
  CategoryPayload,
  GetCategoryListParams,
} from "@/interface/masterManagement";
import { categoryKeys, STALE_5_MIN } from "@/lib/queryKey";
import { getNextPage, isPaginated } from "@/utils/helper";

export const useCategoryList = (params: GetCategoryListParams) =>
  useQuery({
    queryKey: categoryKeys.list(params),
    queryFn: () => getCategoryList(params),
    ...STALE_5_MIN,
  });

export const useInfiniteCategoryList = (params?: GetCategoryListParams) => {
  const hasPagination = isPaginated(params);

  return useInfiniteQuery({
    queryKey: categoryKeys.list({ ...params, type: "infinite" }),
    queryFn: ({ pageParam }) =>
      getCategoryList(
        hasPagination ? { ...params, page: pageParam } : params,
      ),
    initialPageParam: params?.page ?? 1,
    getNextPageParam: (lastPage, allPages) =>
      hasPagination
        ? getNextPage(lastPage, allPages, params?.page_size)
        : undefined,
    ...STALE_5_MIN,
  });
};

export const useCreateCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.lists() });
    },
  });
};

export const useUpdateCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number | string; payload: CategoryPayload }) =>
      updateCategory(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.lists() });
    },
  });
};

export const useDeleteCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number | string) => deleteCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.lists() });
    },
  });
};
